import React, { useCallback } from 'react';
import type { Product } from '../data/site';
import './sections.css';

interface ProductCardProps {
    product: Product;
    /** Detailed variant shows the full feature list and audience line. */
    detailed?: boolean;
    /** Position in the grid — drives the staggered scroll reveal. */
    index?: number;
}

const ProductCard: React.FC<ProductCardProps> = ({ product, detailed = false, index = 0 }) => {
    const features = detailed ? product.features : product.features.slice(0, 4);

    /**
     * Feeds the pointer position to CSS as percentages so the card's accent
     * glow tracks the cursor. Written straight to the node — re-rendering on
     * every mousemove would be far too expensive across a grid of cards.
     */
    const handlePointer = useCallback((e: React.MouseEvent<HTMLElement>) => {
        const el = e.currentTarget;
        const box = el.getBoundingClientRect();
        el.style.setProperty('--mx', `${((e.clientX - box.left) / box.width) * 100}%`);
        el.style.setProperty('--my', `${((e.clientY - box.top) / box.height) * 100}%`);
    }, []);

    return (
        <article
            className={`product-card ${detailed ? 'product-card--detailed' : ''}`}
            style={{ ['--accent' as string]: product.accent, ['--i' as string]: index }}
            data-reveal="up"
            onMouseMove={handlePointer}
        >
            <span className="product-card__spot" aria-hidden="true" />

            <div className="product-card__top">
                <span className="product-card__icon" aria-hidden="true">
                    {product.icon}
                </span>
                <span className="product-card__cat">{product.category}</span>
            </div>

            <h3 className="product-card__name">{product.name}</h3>
            <p className="product-card__tagline">{product.tagline}</p>
            <p className="product-card__desc">{product.description}</p>

            <ul className="product-card__features">
                {features.map((feature) => (
                    <li key={feature}>{feature}</li>
                ))}
            </ul>

            {detailed && (
                <p className="product-card__audience">
                    <strong>Built for</strong> {product.audience}
                </p>
            )}

            <a
                className="link-arrow product-card__link"
                href={product.url}
                target="_blank"
                rel="noopener noreferrer"
            >
                Visit {product.name}
                <span aria-hidden="true">→</span>
            </a>
        </article>
    );
};

export default ProductCard;
